import { FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuthStore } from "@/state/authStore";

/**
 * Troca da própria senha.
 *
 * Serve a dois momentos: o usuário que escolhe trocar pelo menu do perfil e o
 * primeiro acesso, em que a senha foi definida por outra pessoa (instalador ou
 * administrador) e a troca é obrigatória. A diferença entre os dois é só o
 * texto e a ausência do botão de cancelar — o fluxo é o mesmo.
 */

/** Mesma regra do backend (`services/passwords.py`), avaliada enquanto digita. */
const RULES = [
  { label: "Pelo menos 8 caracteres", test: (value: string) => value.length >= 8 },
  { label: "Pelo menos 1 número", test: (value: string) => /\d/.test(value) },
  { label: "Pelo menos 1 letra maiúscula", test: (value: string) => /[A-Z]/.test(value) },
];

export function ChangePassword() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const changePassword = useAuthStore((state) => state.changePassword);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const required = Boolean(user?.must_change_password);
  const checks = useMemo(() => RULES.map((rule) => rule.test(newPassword)), [newPassword]);
  const strongEnough = checks.every(Boolean);
  const matches = newPassword.length > 0 && newPassword === confirmation;
  const canSubmit = strongEnough && matches && currentPassword.length > 0 && !submitting;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      navigate("/manager", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full items-start justify-center overflow-auto px-4 py-10">
      <div className="w-full max-w-md rounded-xl border border-ink-700 bg-ink-850 p-6 shadow-xl">
        <h1 className="text-lg font-semibold tracking-tight text-ink-50">
          {required ? "Defina sua senha" : "Alterar senha"}
        </h1>
        <p className="mt-1 text-xs leading-relaxed text-ink-300">
          {required
            ? "Sua senha atual foi definida por outra pessoa. Escolha uma senha nova para continuar."
            : "Escolha uma nova senha para a sua conta."}
        </p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <label className="block">
            <span className="text-xs text-ink-300">Senha atual</span>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              className="mt-1 w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              required
            />
          </label>

          <label className="block">
            <span className="text-xs text-ink-300">Nova senha</span>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              className="mt-1 w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              required
            />
          </label>

          <ul className="space-y-1">
            {RULES.map((rule, index) => {
              const met = checks[index];
              return (
                <li
                  key={rule.label}
                  className={`flex items-center gap-2 text-[11px] ${
                    met ? "text-emerald-400" : "text-ink-400"
                  }`}
                >
                  <span aria-hidden="true">{met ? "✓" : "•"}</span>
                  {rule.label}
                </li>
              );
            })}
          </ul>

          <label className="block">
            <span className="text-xs text-ink-300">Repita a nova senha</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              className="mt-1 w-full rounded border border-ink-600 bg-ink-900 px-3 py-2 text-sm text-ink-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              required
            />
            {confirmation.length > 0 && !matches && (
              <span className="mt-1 block text-[11px] text-amber-400">As senhas não coincidem.</span>
            )}
          </label>

          {error && (
            <p role="alert" className="rounded border border-red-800 bg-red-950/50 px-3 py-2 text-xs text-red-300">
              {error}
            </p>
          )}

          <div className="flex items-center gap-2 pt-1">
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded border border-emerald-600 bg-emerald-900/50 px-4 py-2 text-sm text-emerald-100 hover:bg-emerald-900/80 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? "Salvando..." : "Salvar nova senha"}
            </button>
            {!required && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="rounded border border-ink-600 bg-ink-800 px-4 py-2 text-sm text-ink-200 hover:border-ink-500"
              >
                Cancelar
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
